import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';


export interface DateCriteria {
  fromDate: string; // 'YYYY-MM-DD'
  fromHour: string; // '00' - '23'
  fromMinutes: string; // '00' - '59'
  toDate?: string; // 'YYYY-MM-DD'
  toHour?: string; // '00' - '23'
  toMinutes?: string; // '00' - '59'
}

export interface DateCriteriaISO {
  fromDate: string; // 'YYYY-MM-DDTHH:MM'
  toDate?: string; // 'YYYY-MM-DDTHH:MM'
}

export interface DateObj {
  date: string; // 'YYYY-MM-DD'
  hour: string; // '00' - '23'
  minutes: string; // '00' - '59'
}

function check(value: string | null | undefined, defaultValue: string): string {
  return (!isDefined(value) || value === '') ? defaultValue : value;
}

@Injectable({ providedIn: 'root' })
export class DatesService {

  private readonly AKAMAI_DATE_FORMAT: string = 'yyyy-LL-dd\'T\'HH:mm:ss\'Z\'';
  private readonly PARAM_DATE_FORMAT: string = 'yyyy-LL-dd\'T\'HH:mm:ss';
  private readonly SEGMENT_DATE_FORMAT: string = 'y-LL-dd\'T\'HH:mm:ss';
  private timeTravelTo: string | undefined;

  private readonly DEFAULT_TIME_ZONE: string = 'Europe/Madrid';
  private readonly timezonesByCountryCode: Record<string, string> = {
    AD: 'Europe/Andorra',
    AE: 'Asia/Dubai',
    AF: 'Asia/Kabul',
    AG: 'America/Antigua',
    AI: 'America/Anguilla',
    AL: 'Europe/Tirane',
    AM: 'Asia/Yerevan',
    AO: 'Africa/Luanda',
    AQ: 'Antarctica/McMurdo',
    AR: 'America/Argentina/Buenos_Aires',
    AS: 'Pacific/Pago_Pago',
    AT: 'Europe/Vienna',
    AU: 'Australia/Sydney',
    AW: 'America/Aruba',
    AX: 'Europe/Mariehamn',
    AZ: 'Asia/Baku',
    BA: 'Europe/Sarajevo',
    BB: 'America/Barbados',
    BD: 'Asia/Dhaka',
    BE: 'Europe/Brussels',
    BF: 'Africa/Ouagadougou',
    BG: 'Europe/Sofia',
    BH: 'Asia/Bahrain',
    BI: 'Africa/Bujumbura',
    BJ: 'Africa/Porto-Novo',
    BL: 'America/St_Barthelemy',
    BM: 'Atlantic/Bermuda',
    BN: 'Asia/Brunei',
    BO: 'America/La_Paz',
    BQ: 'America/Kralendijk',
    BR: 'America/Sao_Paulo',
    BS: 'America/Nassau',
    BT: 'Asia/Thimphu',
    BW: 'Africa/Gaborone',
    BY: 'Europe/Minsk',
    BZ: 'America/Belize',
    CA: 'America/Toronto',
    CC: 'Indian/Cocos',
    CD: 'Africa/Kinshasa',
    CF: 'Africa/Bangui',
    CG: 'Africa/Brazzaville',
    CH: 'Europe/Zurich',
    CI: 'Africa/Abidjan',
    CK: 'Pacific/Rarotonga',
    CL: 'America/Santiago',
    CM: 'Africa/Douala',
    CN: 'Asia/Shanghai',
    CO: 'America/Bogota',
    CR: 'America/Costa_Rica',
    CU: 'America/Havana',
    CV: 'Atlantic/Cape_Verde',
    CW: 'America/Curacao',
    CX: 'Indian/Christmas',
    CY: 'Asia/Nicosia',
    CZ: 'Europe/Prague',
    DE: 'Europe/Berlin',
    DJ: 'Africa/Djibouti',
    DK: 'Europe/Copenhagen',
    DM: 'America/Dominica',
    DO: 'America/Santo_Domingo',
    DZ: 'Africa/Algiers',
    EC: 'America/Guayaquil',
    EE: 'Europe/Tallinn',
    EG: 'Africa/Cairo',
    EH: 'Africa/El_Aaiun',
    ER: 'Africa/Asmara',
    ES: 'Europe/Madrid',
    ET: 'Africa/Addis_Ababa',
    FI: 'Europe/Helsinki',
    FJ: 'Pacific/Fiji',
    FK: 'Atlantic/Stanley',
    FM: 'Pacific/Chuuk',
    FO: 'Atlantic/Faroe',
    FR: 'Europe/Paris',
    GA: 'Africa/Libreville',
    GB: 'Europe/London',
    GD: 'America/Grenada',
    GE: 'Asia/Tbilisi',
    GF: 'America/Cayenne',
    GG: 'Europe/Guernsey',
    GH: 'Africa/Accra',
    GI: 'Europe/Gibraltar',
    GL: 'America/Godthab',
    GM: 'Africa/Banjul',
    GN: 'Africa/Conakry',
    GP: 'America/Guadeloupe',
    GQ: 'Africa/Malabo',
    GR: 'Europe/Athens',
    GS: 'Atlantic/South_Georgia',
    GT: 'America/Guatemala',
    GU: 'Pacific/Guam',
    GW: 'Africa/Bissau',
    GY: 'America/Guyana',
    HK: 'Asia/Hong_Kong',
    HN: 'America/Tegucigalpa',
    HR: 'Europe/Zagreb',
    HT: 'America/Port-au-Prince',
    HU: 'Europe/Budapest',
    IC: 'Atlantic/Canary',
    ID: 'Asia/Jakarta',
    IE: 'Europe/Dublin',
    IL: 'Asia/Jerusalem',
    IM: 'Europe/Isle_of_Man',
    IN: 'Asia/Kolkata',
    IO: 'Indian/Chagos',
    IQ: 'Asia/Baghdad',
    IR: 'Asia/Tehran',
    IS: 'Atlantic/Reykjavik',
    IT: 'Europe/Rome',
    JE: 'Europe/Jersey',
    JM: 'America/Jamaica',
    JO: 'Asia/Amman',
    JP: 'Asia/Tokyo',
    KE: 'Africa/Nairobi',
    KG: 'Asia/Bishkek',
    KH: 'Asia/Phnom_Penh',
    KI: 'Pacific/Tarawa',
    KM: 'Indian/Comoro',
    KN: 'America/St_Kitts',
    KP: 'Asia/Pyongyang',
    KR: 'Asia/Seoul',
    KW: 'Asia/Kuwait',
    KY: 'America/Cayman',
    KZ: 'Asia/Almaty',
    LA: 'Asia/Vientiane',
    LB: 'Asia/Beirut',
    LC: 'America/St_Lucia',
    LI: 'Europe/Vaduz',
    LK: 'Asia/Colombo',
    LR: 'Africa/Monrovia',
    LS: 'Africa/Maseru',
    LT: 'Europe/Vilnius',
    LU: 'Europe/Luxembourg',
    LV: 'Europe/Riga',
    LY: 'Africa/Tripoli',
    MA: 'Africa/Casablanca',
    MC: 'Europe/Monaco',
    MD: 'Europe/Chisinau',
    ME: 'Europe/Podgorica',
    MF: 'America/Marigot',
    MG: 'Indian/Antananarivo',
    MH: 'Pacific/Majuro',
    MK: 'Europe/Skopje',
    ML: 'Africa/Bamako',
    MM: 'Asia/Yangon',
    MN: 'Asia/Ulaanbaatar',
    MO: 'Asia/Macau',
    MP: 'Pacific/Saipan',
    MQ: 'America/Martinique',
    MR: 'Africa/Nouakchott',
    MS: 'America/Montserrat',
    MT: 'Europe/Malta',
    MU: 'Indian/Mauritius',
    MV: 'Indian/Maldives',
    MW: 'Africa/Blantyre',
    MX: 'America/Mexico_City',
    MY: 'Asia/Kuala_Lumpur',
    MZ: 'Africa/Maputo',
    NA: 'Africa/Windhoek',
    NC: 'Pacific/Noumea',
    NE: 'Africa/Niamey',
    NF: 'Pacific/Norfolk',
    NG: 'Africa/Lagos',
    NI: 'America/Managua',
    NL: 'Europe/Amsterdam',
    NO: 'Europe/Oslo',
    NP: 'Asia/Kathmandu',
    NR: 'Pacific/Nauru',
    NU: 'Pacific/Niue',
    NZ: 'Pacific/Auckland',
    OM: 'Asia/Muscat',
    PA: 'America/Panama',
    PE: 'America/Lima',
    PF: 'Pacific/Tahiti',
    PG: 'Pacific/Port_Moresby',
    PH: 'Asia/Manila',
    PK: 'Asia/Karachi',
    PL: 'Europe/Warsaw',
    PM: 'America/Miquelon',
    PN: 'Pacific/Pitcairn',
    PR: 'America/Puerto_Rico',
    PS: 'Asia/Gaza',
    PT: 'Europe/Lisbon',
    PW: 'Pacific/Palau',
    PY: 'America/Asuncion',
    QA: 'Asia/Qatar',
    RE: 'Indian/Reunion',
    RO: 'Europe/Bucharest',
    RS: 'Europe/Belgrade',
    RU: 'Europe/Moscow',
    RW: 'Africa/Kigali',
    SA: 'Asia/Riyadh',
    SB: 'Pacific/Guadalcanal',
    SC: 'Indian/Mahe',
    SD: 'Africa/Khartoum',
    SE: 'Europe/Stockholm',
    SG: 'Asia/Singapore',
    SH: 'Atlantic/St_Helena',
    SI: 'Europe/Ljubljana',
    SJ: 'Arctic/Longyearbyen',
    SK: 'Europe/Bratislava',
    SL: 'Africa/Freetown',
    SM: 'Europe/San_Marino',
    SN: 'Africa/Dakar',
    SO: 'Africa/Mogadishu',
    SR: 'America/Paramaribo',
    SS: 'Africa/Juba',
    ST: 'Africa/Sao_Tome',
    SV: 'America/El_Salvador',
    SX: 'America/Lower_Princes',
    SY: 'Asia/Damascus',
    SZ: 'Africa/Mbabane',
    TC: 'America/Grand_Turk',
    TD: 'Africa/Ndjamena',
    TF: 'Indian/Kerguelen',
    TG: 'Africa/Lome',
    TH: 'Asia/Bangkok',
    TJ: 'Asia/Dushanbe',
    TK: 'Pacific/Fakaofo',
    TL: 'Asia/Dili',
    TM: 'Asia/Ashgabat',
    TN: 'Africa/Tunis',
    TO: 'Pacific/Tongatapu',
    TR: 'Europe/Istanbul',
    TT: 'America/Port_of_Spain',
    TV: 'Pacific/Funafuti',
    TW: 'Asia/Taipei',
    TZ: 'Africa/Dar_es_Salaam',
    UA: 'Europe/Kiev',
    UG: 'Africa/Kampala',
    UM: 'Pacific/Wake',
    US: 'America/New_York',
    UY: 'America/Montevideo',
    UZ: 'Asia/Tashkent',
    VA: 'Europe/Vatican',
    VC: 'America/St_Vincent',
    VE: 'America/Caracas',
    VG: 'America/Tortola',
    VI: 'America/St_Thomas',
    VN: 'Asia/Ho_Chi_Minh',
    VU: 'Pacific/Efate',
    WF: 'Pacific/Wallis',
    WS: 'Pacific/Apia',
    YE: 'Asia/Aden',
    YT: 'Indian/Mayotte',
    ZA: 'Africa/Johannesburg',
    ZM: 'Africa/Lusaka',
    ZW: 'Africa/Harare',
    WW: 'Europe/Madrid',
    XE: 'Asia/Dubai'
  };

  constructor(
    private readonly http: HttpClient
  ) {}

  public setTimeTravelTo(timeTravelTo: string) {
    this.timeTravelTo = timeTravelTo;
  }

  public getTimeZoneFromCountry(country: string): string {
    const tz: string | undefined = this.timezonesByCountryCode[country];
    return tz ?? this.DEFAULT_TIME_ZONE;
  }

  public getCurrentDate(country: string, timeTravelTo?: string): Observable<DateTime> {
    const tz: string = this.getTimeZoneFromCountry(country);
    const timeTravel = timeTravelTo || this.timeTravelTo;

    if (timeTravel) {
      return of(DateTime.fromFormat(timeTravel, this.PARAM_DATE_FORMAT, { zone: tz }).toUTC());
    }
    const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
    return this.http.get('https://time.akamai.com/?iso', { headers, responseType: 'text' })
      .pipe(
        switchMap((date: any) => {
          const correctString = `${DateTime.fromFormat(date, this.AKAMAI_DATE_FORMAT).setZone(tz).toFormat(this.PARAM_DATE_FORMAT)}Z`;
          return of(DateTime.fromFormat(correctString, this.AKAMAI_DATE_FORMAT, { zone: 'utc' }));
        }),
        catchError(this.handleError) // eslint-disable-line @typescript-eslint/unbound-method
      );
  }

  /**
   * @param {(string | number)} date luxon DateTime ISO string or milliseconds since (1 January 1970 00:00:00 UTC)
   * @returns {string} 'YYYY-MM-DD HH:MM'
   */
  public formatSingleDateFromISO(date: string | number): string {
    return (typeof date === 'number' ? DateTime.fromMillis(date) : DateTime.fromISO(date)).toFormat('yyyy-LL-dd HH:mm:ss');
  }

  public transformDateCriteriaToISO(segmentDate: DateCriteria): DateCriteriaISO {
    const { fromDate, toDate } = segmentDate;
    const transformedSegmentationDate: DateCriteriaISO = { fromDate, toDate };

    let from: string;
    if (segmentDate.fromDate && (segmentDate.fromHour || segmentDate.fromMinutes)) {
      from = `${segmentDate.fromDate} ${segmentDate.fromHour || '00'}:${segmentDate.fromMinutes || '00'}`;
    } else {
      from = `${segmentDate.fromDate} 00:00`;
    }
    const fromISO = DateTime.fromSQL(from).toFormat(this.PARAM_DATE_FORMAT);
    transformedSegmentationDate.fromDate = fromISO;

    let to: DateTime | null = null;
    if (segmentDate.toDate) {
      to = DateTime.fromSQL(`${segmentDate.toDate} ${check(segmentDate.toHour, '23')}:${check(segmentDate.toMinutes, '59')}:59`);
    }

    if (to && to.isValid) {
      transformedSegmentationDate.toDate = to.toFormat(this.PARAM_DATE_FORMAT);
    } else {
      transformedSegmentationDate.toDate = undefined;
    }

    return transformedSegmentationDate;
  }

  public transformDateCriteriasToISO(dateCriteria: DateCriteria[]): DateCriteriaISO[] {
    return dateCriteria.map((segmentDate) => this.transformDateCriteriaToISO(segmentDate));
  }

  /**
   * @param {DateTime} date luxon DateTime object
   * @returns {DateObj} { hour: '00' - '23', minutes: '00' - '59', date: 'YYYY-MM-DD' }
   */
  public formatDateObject(date: DateTime): DateObj {
    return {
      date: date.toFormat('yyyy-LL-dd'),
      hour: date.toFormat('HH'),
      minutes: date.toFormat('mm')
    };
  }

  public transformDateCriteriaFromISO(segmentDate: DateCriteriaISO): DateCriteria {
    const transformedSegmentDate: any = {
      ...segmentDate
    };

    const from = DateTime.fromISO(segmentDate.fromDate);
    if (from.isValid) {
      const dateObj = this.formatDateObject(from);
      transformedSegmentDate.fromHour = dateObj.hour;
      transformedSegmentDate.fromMinutes = dateObj.minutes;
      transformedSegmentDate.fromDate = dateObj.date;
    } else {
      transformedSegmentDate.fromHour = null;
      transformedSegmentDate.fromMinutes = null;
      transformedSegmentDate.fromDate = null;
    }

    const to = segmentDate.toDate && DateTime.fromISO(segmentDate.toDate);
    if (to && to.isValid) {
      const dateObj = this.formatDateObject(to);
      transformedSegmentDate.toHour = dateObj.hour;
      transformedSegmentDate.toMinutes = dateObj.minutes;
      transformedSegmentDate.toDate = dateObj.date;
    } else {
      transformedSegmentDate.toHour = null;
      transformedSegmentDate.toMinutes = null;
      transformedSegmentDate.toDate = null;
    }

    return transformedSegmentDate as DateCriteria;
  }

  public transformDateCriteriasFromISO(dateCriteria: DateCriteriaISO[]): DateCriteria[] {
    return dateCriteria.map((segmentDate) => this.transformDateCriteriaFromISO(segmentDate));
  }

  public matchesDates(currentdt: DateTime, criteriaDates: any[]): boolean {
    return criteriaDates.some((sdate) => {
      return this.matchesSingleDate(currentdt, sdate);
    });
  }

  public isBetween(fromDate: DateTime, toDate: DateTime, currentDt: DateTime, inclusivity: '[]' | '(]' | '[)' | '()' = '()'): boolean {
    const fromDateSeconds = fromDate.toSeconds();
    const toDateSeconds = toDate.toSeconds();
    const currentDateSeconds = currentDt.toSeconds();

    switch (inclusivity) {
      case '[]':
        return currentDateSeconds >= fromDateSeconds && currentDateSeconds <= toDateSeconds;
      case '[)':
        return currentDateSeconds >= fromDateSeconds && currentDateSeconds < toDateSeconds;
      case '(]':
        return currentDateSeconds > fromDateSeconds && currentDateSeconds <= toDateSeconds;
      default:
        return currentDateSeconds > fromDateSeconds && currentDateSeconds < toDateSeconds;
    }
  }

  private handleError(error: any): Promise<DateTime> {
    console.error('An error occurred', error);
    return Promise.resolve(DateTime.now());
  }

  private matchesSingleDate(currentdt: DateTime, sdate: any): boolean {
    const predicates = [
      /* eslint-disable @typescript-eslint/unbound-method */
      this.matchDateFromTodayOnwards,
      this.matchDateInRange
      /* eslint-enable @typescript-eslint/unbound-method */
    ];

    return predicates.some((predicate) => predicate(sdate, currentdt, this.SEGMENT_DATE_FORMAT));
  }

  // Match: No repeat, only from date
  private matchDateFromTodayOnwards(sdate: any, currentdt: DateTime, format: string): boolean {
    if (sdate.fromDate && !sdate.toDate) {
      const fromDate: DateTime = DateTime.fromFormat(sdate.fromDate, format, { zone: 'utc' });

      return currentdt >= fromDate;
    }

    return false;
  }

  // Match: No repeat, from and to dates
  private matchDateInRange(sdate: any, currentdt: DateTime, format: string): boolean {
    if (sdate.fromDate && sdate.toDate) {
      const fromDateSeconds = DateTime.fromFormat(sdate.fromDate, format, { zone: 'utc' }).toSeconds();
      const toDateSeconds = DateTime.fromFormat(sdate.toDate, format, { zone: 'utc' }).toSeconds();
      const currentDateSeconds = currentdt.toSeconds();

      return currentDateSeconds >= fromDateSeconds && currentDateSeconds <= toDateSeconds;
    }

    return false;
  }

}
